import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getMessageNumbers, getSmsKeywords } from 'api/engage';
import * as DeleteKeyword from 'assets/js/pages/engage/components/DeleteKeyword';
import { SmsKeywordFormModal } from 'assets/js/pages/engage/components/Modals/SmsKeywordFormModal';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Table from 'components/table';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { first, truncate } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { SmsKeyword } from 'types/domain';

const columnHelper = createColumnHelper<SmsKeyword>();

const KeywordSettings = () => {
    const [selectedSmsKeyword, setSelectedSmsKeyword] = useState<SmsKeyword | null>(null);
    const [showSmsKeywordModal, setShowSmsKeywordModal] = useState(false);
    const { accountId } = useCurrentAccount();

    const { data: messageNumbers, isLoading: messageNumbersLoading } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageMessageNumbers(accountId),
        queryFn: () => getMessageNumbers(accountId),
    });

    const { data: smsKeywords, isLoading: smsKeywordsLoading } = useSuspenseQuery({
        queryKey: accountQueryKeys.engageSmsKeywords(accountId),
        queryFn: () => conditionallyFetchOrNull(() => getSmsKeywords(accountId), !!messageNumbers),
    });

    const isLoading = messageNumbersLoading || smsKeywordsLoading;

    const firstMessageNumber = useMemo(() => first(messageNumbers), [messageNumbers]);

    const columns = useMemo(
        () => [
            columnHelper.accessor('keyword', {
                id: 'keyword',
                header: 'Keyword',
                cell: info => {
                    return <>{info.row.original.keyword}</>;
                },
            }),

            columnHelper.accessor('keyword', {
                id: 'response',
                header: 'Response',
                cell: info => {
                    return <>{truncate(info.row.original.response, { length: 50 })}</>;
                },
            }),

            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const keyword = info.row.original;

                    return (
                        <DropdownMenu.Root key={keyword.id}>
                            <Table.RowDropdownTrigger />
                            <DropdownMenu.Content sideOffset={5} align={'end'}>
                                <DropdownMenu.Item
                                    onSelect={e => e.preventDefault()}
                                    onClick={() => {
                                        setShowSmsKeywordModal(true);
                                        setSelectedSmsKeyword(keyword);
                                    }}
                                >
                                    Edit
                                </DropdownMenu.Item>
                                <DeleteKeyword.Root keywordId={keyword.id}>
                                    <DeleteKeyword.Trigger asChild>
                                        <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                                            Delete
                                        </DropdownMenu.Item>
                                    </DeleteKeyword.Trigger>
                                </DeleteKeyword.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
                meta: {
                    className: '!pl-2',
                },
            }),
        ],
        [],
    );

    const table = useReactTable({
        data: smsKeywords ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
    });

    return (
        <>
            <Box className="overflow-hidden">
                <div className="flex flex-col">
                    <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-4">
                        <p className="text-2xl font-medium">SMS Keywords</p>
                        <Button
                            variant="outline"
                            color="neutral"
                            size="sm"
                            onClick={() => setShowSmsKeywordModal(true)}
                        >
                            Add SMS Keyword
                        </Button>
                    </div>

                    {firstMessageNumber ? (
                        <div className="relative overflow-x-auto">
                            <Table.Root table={table} variant="bordered" isLoading={isLoading}>
                                <Table.Content className="w-full">
                                    <Table.Header />
                                    <Table.Body />
                                </Table.Content>
                                <Table.Empty variant={'secondary'}>
                                    <Table.EmptyIcon />
                                    <Table.EmptyTitle>No SMS Keywords found</Table.EmptyTitle>
                                </Table.Empty>
                            </Table.Root>
                        </div>
                    ) : (
                        <div className="border-t p-8">
                            <p>
                                <FontAwesomeIcon icon={faTriangleExclamation} className="mr-1" />
                                You need to{' '}
                                <Link to={`/accounts/${accountId}/engage/settings/sms`}>
                                    provision an SMS number
                                </Link>{' '}
                                before you can create custom SMS keywords.
                            </p>
                        </div>
                    )}
                </div>
            </Box>

            {firstMessageNumber && (
                <SmsKeywordFormModal
                    key={selectedSmsKeyword?.id}
                    existingRecord={selectedSmsKeyword}
                    open={showSmsKeywordModal}
                    messageNumber={firstMessageNumber}
                    onClose={() => {
                        setSelectedSmsKeyword(null);
                        setShowSmsKeywordModal(false);
                    }}
                />
            )}
        </>
    );
};

export default KeywordSettings;
